<template>
    <div class="row">
        <div class="d-flex justify-content-end align-items-center flex-wrap">
            <div class="block-options content-heading">
                <select class="btn-block-option" v-model="perPage" @change="updatePerPage">
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                </select>
            </div>
        </div>
    </div>

    <!-- Accounts Table -->
    <div class="block-rounded block">
        <div class="block-header block-header-default d-flex justify-content-between align-items-center">
            <div>
                <h3 class="block-title m-0 d-flex align-items-center justify-content-between flex-wrap">
                    <div>
                        {{ $t('title_result') }} <span>{{ filteredAccounts.length }}</span>
                        <span v-if="selectedAccounts.length > 0" class="text-muted ms-2">
                            ( {{ $t('title_selected') }} {{ selectedAccounts.length }} )
                        </span>
                    </div>
                    <div v-if="selectedAccounts.length > 0" class="btn-group mt-2 mt-md-0 ms-md-3" role="group">
                        <button v-if="selectedAccounts.length !== filteredAccounts.length" type="button"
                            class="btn btn-sm btn-alt-primary" @click="toggleSelectAll">
                            <i class="fa-solid fa-list-check me-1"></i> Select All
                        </button>
                        <button v-else type="button" class="btn btn-sm btn-alt-secondary" @click="deselectAll">
                            <i class="fa-solid fa-xmark me-1"></i> Clear All
                        </button>
                    </div>
                </h3>
            </div>
        </div>

        <div class="block-content block-content-full">
            <div class="table-responsive">
                <table class="table-sm table-vcenter fs-sm table-hover table w-100" style="table-layout: auto">
                    <thead>
                        <tr class="text-center fs-6">
                            <th class="text-center" style="width: 30%;">{{ $t('table_account_title') }}</th>
                            <th class="text-center" style="width: 20%;">{{ $t('table_account_type') }}</th>
                            <th class="text-center" style="width: 20%;">{{ $t('table_branch') }}</th>
                            <th class="text-center" style="width: 10%;">{{ $t('table_balance') }}</th>
                            <th class="text-center" style="width: 15%;">{{ $t('table_created_by') }}</th>
                            <th class="text-center" style="width: 15%;">{{ $t('table_created_at') }}</th>
                            <th class="text-center" style="width: 10%;"></th>
                        </tr>
                        <tr>
                            <th>
                                <IconField>
                                    <InputIcon class="pi pi-search" />
                                    <InputText v-model="filters.titleSearch" type="text" size="small"
                                        class="w-100 h-9" />
                                </IconField>
                            </th>
                            <th>
                                <MultiSelect v-model="filters.typeFilter" :placeholder="$t('placeholder.any')" :options="accountTypes"
                                    optionLabel="title_ar" optionValue="_id" :maxSelectedLabels="3" size="small"
                                    class="w-full text-sm h-9">
                                    <template #option="slotProps">
                                        <div class="text-sm">
                                            <span class="badge p-2 text-uppercase"
                                                :class="`bg-${slotProps.option.color}`">
                                                {{ slotProps.option.title_ar }}
                                            </span>
                                        </div>
                                    </template>
                                </MultiSelect>
                            </th>
                            <th>
                                <IconField>
                                    <InputIcon class="pi pi-search" />
                                    <InputText v-model="filters.branchFilter" type="text" size="small"
                                        class="w-100 h-9" />
                                </IconField>
                            </th>
                            <th>
                                <div class="flex gap-2">
                                    <InputNumber v-model="filters.balanceMin" placeholder="Min" size="small"
                                        class="h-9" />
                                    <!-- <InputNumber v-model="filters.balanceMax" placeholder="Max" size="small"
                                        class="h-9" /> -->
                                </div>
                            </th>
                            <th>
                                <MultiSelect v-model="filters.createdByFilter" :options="users" optionLabel="first_name"
                                    optionValue="_id" :placeholder="$t('placeholder.any')" filter :maxSelectedLabels="3"
                                    class="w-full text-sm h-9" :selectionLimit="null">
                                    <template #option="slotProps">
                                        <div class="text-sm flex items-center text-sm gap-2">
                                            <img :alt="slotProps.option.name" :src="slotProps.option.avatar"
                                                class="w-8 h-8 rounded-full object-cover" style="width: 32px" />
                                            <span>{{ slotProps.option.first_name }} {{ slotProps.option.last_name
                                            }}</span>
                                        </div>
                                    </template>
                                </MultiSelect>
                            </th>
                            <th>
                                <DatePicker v-model="filters.createdAtSearch" showIcon fluid iconDisplay="input"
                                    class="h-9" size="small" />
                            </th>
                            <th class="text-center align-middle">
                                <button type="button" class="btn btn-alt-secondary btn-sm" title="Clear Filters"
                                    @click="clearFilters">
                                    <i class="fa fa-solid fa-trash"></i>
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(account, index) in paginatedAccounts" :key="account._id" class="text-center" :class="{
                            'table-primary selected-row': isSelected(account)
                        }" @click="selectAccount(account, index, $event)">
                            <td class="text-truncate" style="max-width: 90px">
                                <div class="d-flex align-items-center">
                                    <img v-if="account.user" :alt="account.avatar" :src="account.user.avatar"
                                        class="rounded-circle me-2" style="width: 32px; height: 32px;" />
                                    <span v-else class="me-2"
                                        style="width: 32px; height: 32px; display: flex; justify-content: center; align-items: center;">
                                        <i class="fa-solid fa-vault" style="font-size: 24px; color: #a6a6a6;"></i>
                                    </span>
                                    <a :href="`/partner/account/details/view/${account._id}`"
                                        @click.prevent="navigateTo(account._id)" @click.stop>
                                        {{ account.title }}
                                    </a>
                                </div>
                            </td>

                            <td>
                                <span class="badge p-2 text-uppercase"
                                :class="`bg-${account.type.color}`">
                                    {{ account.type.title_ar }}
                                </span>
                            </td>

                            <td>
                                {{ account.branch?.title }}
                            </td>

                            <td>
                                {{ formatNumber(account.balance) }} {{ defaultCurrencyAcronym }}
                            </td>
                            <td>
                                <template v-if="account.creator?.first_name">
                                    <a :href="`/profile/${account.created_by}`" @click.stop>
                                        {{ account.creator.first_name }} {{ account.creator.last_name }}
                                    </a>
                                </template>
                                <span v-else class="badge bg-danger">N/A</span>
                            </td>
                            <td class="text-truncate">{{ formatDate(account.created_at) }}</td>
                            <td>
                                <template v-if="account.is_default">
                                    <span
                                        class="fs-xs fw-semibold d-inline-block py-1 px-3 rounded-pill bg-warning-light text-warning">
                                        {{ $t('default') }}
                                    </span>
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <nav aria-label="Page navigation">
        <ul class="pagination justify-content-end push">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="prevPage">
                    <i class="fa fa-angle-left"></i>
                </a>
            </li>
            <li v-for="page in pages" :key="page" class="page-item" :class="{ active: page === currentPage }">
                <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <a class="page-link" href="#" @click.prevent="nextPage">
                    <i class="fa fa-angle-right"></i>
                </a>
            </li>
        </ul>
    </nav>
</template>

<script>
import MultiSelect from 'primevue/multiselect';
import DatePicker from 'primevue/datepicker';
import InputNumber from 'primevue/inputnumber';
import axios from 'axios';

function debounce(fn, delay) {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), delay);
    };
}

export default {
    components: {
        MultiSelect,
        DatePicker,
        InputNumber
    },
    props: {
        accounts: {
            type: Array,
            default: () => []
        },
        accountTypes: {
            type: Array,
            default: () => ['asset', 'liability', 'equity', 'income', 'expense']
        },
        initialSelectedAccounts: {
            type: Array,
            default: () => []
        },
        defaultCurrencyAcronym: {
            type: String,
            default: 'EGP'
        }
    },

    data() {
        return {
            loading: true,
            perPage: 50,
            currentPage: 1,
            selectedAccounts: [], // Local state for selected accounts
            lastSelectedIndex: null, // for shift-click range
            filters: {
                titleSearch: null,
                typeFilter: [],
                branchFilter: [],
                balanceMin: null,
                createdByFilter: [],
                createdAtSearch: null
            },
            users: [],
            isExporting: false,
            printing: false
        }
    },

    computed: {
        accountOptions() {
            return this.accounts.map(account => ({
                _id: account._id,
                title: account.title
            }));
        },

        filteredAccounts() {
            const {
                referenceSearch,
                branchFilter,
                typeFilter,
                balanceMin,
                createdByFilter,
                createdAtSearch
            } = this.filters;

            return this.accounts.filter(account => {
                // Reference search
                const matchesTitle = !referenceSearch ||
                    (account.title && account.title.toLowerCase().includes(referenceSearch.toLowerCase()));

                // Branch filter
                const matchesBranch = branchFilter.length === 0 || branchFilter.includes(account.branch_id);

                // Type filter
                const matchesType = typeFilter.length === 0 || typeFilter.includes(account.type_id);

                // Balance range
                const matchesBalance = (!balanceMin || account.balance == balanceMin);

                // Created by filter
                const matchesCreatedBy = createdByFilter.length === 0 ||
                    (account.created_by && createdByFilter.includes(account.created_by));

                // Created at date filter
                const matchesCreatedAt = !createdAtSearch ||
                    (account.created_at && new Date(account.created_at).toDateString() === new Date(createdAtSearch).toDateString());

                return matchesTitle &&
                    matchesBranch &&
                    matchesType &&
                    matchesBalance &&
                    matchesCreatedBy &&
                    matchesCreatedAt;
            });
        },

        selectedAccountIds() {
            return this.selectedAccounts;
        },

        paginatedAccounts() {
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return this.filteredAccounts.slice(start, end);
        },

        totalPages() {
            return Math.ceil(this.filteredAccounts.length / this.perPage);
        },

        pages() {
            const range = [];
            const start = Math.max(1, this.currentPage - 2);
            const end = Math.min(this.totalPages, this.currentPage + 2);

            for (let i = start; i <= end; i++) {
                range.push(i);
            }

            return range;
        }
    },

    watch: {
        filters: {
            handler: debounce(function () {
                this.currentPage = 1;
                this.$emit('filters-updated', {
                    ...this.filters,
                    page: this.currentPage,
                    perPage: this.perPage
                });
            }, 500),
            deep: true,
            immediate: false
        },
        initialSelectedAccounts: {
            handler(newVal) {
                this.selectedAccounts = [...newVal];
            },
            immediate: true
        },
    },

    methods: {
        clearFilters() {
            this.filters = {
                referenceSearch: null,
                branchFilter: [],
                typeFilter: [],
                balanceMin: null,
                balanceMax: null,
                createdByFilter: [],
                createdAtSearch: null
            };
            this.currentPage = 1;
        },

        selectAccount(account, index, event) {
            let updatedSelection = [...this.selectedAccounts];
            const accountId = account._id;

            if (event.shiftKey && this.lastSelectedIndex !== null) {
                // Shift-click selection logic
                const start = Math.min(index, this.lastSelectedIndex);
                const end = Math.max(index, this.lastSelectedIndex);

                for (let i = start; i <= end; i++) {
                    const s = this.paginatedAccounts[i];
                    const sId = s._id;
                    if (!updatedSelection.includes(sId)) {
                        updatedSelection.push(sId);
                    }
                }
            } else {
                // Single click selection logic
                const existingIndex = updatedSelection.indexOf(accountId);
                if (existingIndex !== -1) {
                    updatedSelection.splice(existingIndex, 1);
                } else {
                    updatedSelection.push(accountId);
                }
                this.lastSelectedIndex = index;
            }

            this.selectedAccounts = updatedSelection;
            this.$emit('selection-changed', updatedSelection);
        },

        isSelected(account) {
            return this.selectedAccounts.includes(account._id);
        },

        toggleSelectAll() {
            const newSelection = this.selectedAccounts.length === this.filteredAccounts.length ?
                [] :
                this.filteredAccounts.map(s => s._id);
            this.updateSelectedAccounts(newSelection);
        },

        deselectAll() {
            this.updateSelectedAccounts([]);
        },

        updateSelectedAccounts(updatedSelection) {
            this.selectedAccounts = updatedSelection;
            this.$emit('selection-changed', updatedSelection);
        },

        updatePerPage() {
            this.currentPage = 1;
        },

        prevPage() {
            if (this.currentPage > 1) this.currentPage--;
        },

        nextPage() {
            if (this.currentPage < this.totalPages) this.currentPage++;
        },

        goToPage(page) {
            this.currentPage = page;
        },

        navigateTo(url) {
            window.location.href = url;
        },

        formatNumber(number) {
            return new Intl.NumberFormat().format(number);
        },

        formatDate(date) {
            return new Date(date).toLocaleDateString();
        },

        async fetchUsers() {
            try {
                const response = await axios.get('/get-all-clients');
                this.users = response.data;
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        }
    },

    mounted() {
        this.fetchUsers();
    }
}
</script>



<style scoped>
.skeleton-loader {
    height: 20px;
    background-color: #f0f0f0;
    border-radius: 4px;
    margin: 5px 0;
    animation: pulse 1.5s infinite ease-in-out;
}

@keyframes pulse {
    0% {
        opacity: 0.6;
    }

    50% {
        opacity: 0.3;
    }

    100% {
        opacity: 0.6;
    }
}

table {
    table-layout: auto;
    width: 100%;
}

td {
    white-space: normal !important;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
}

tr {
    cursor: pointer;
}

.status-badge:hover {
    opacity: 0.8;
}

.client_sim_number span {
    cursor: pointer;
}

.client_sim_number span:hover {
    text-decoration: underline;
}

.dropdown-menu {
    max-height: 250px;
    /* Set your desired maximum height */
    overflow-y: auto;
    /* Enable vertical scrolling when content overflows */
}

.table-active {
    background-color: #0056b3 !important;
    /* Optional: change the background for active state */
}

.selected-row {
    background-color: #007bff !important;
    /* Change to the color you want */
    color: white;
    /* Optional: change text color for contrast */
}
</style>